import type { IAddCancellationCmd, IAddCancellationPositionData } from "@/data/commands/order/AddCancellationCmd";
import type {
    IGeneratePaymentMethodsReportDataCmd,
    IGenerateSalesReportDataCmd,
} from "@/data/commands/order/GenerateSalesReportDataCmd";
import type { IOrderExportCmd } from "@/data/commands/order/OrderExportCmd";
import type { ISalesReportExportCmd } from "@/data/commands/order/SalesReportExportCmd";
import type { ISendReceiptMailCmd } from "@/data/commands/order/SendReceiptMailCmd";
import type { ITransactionExportCmd } from "@/data/commands/order/TransactionExportCmd";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import type {
    ICancellation,
    ICancellationPayment,
    ICancellationPosition,
    ICancellationPositionPayment,
    ICancellationPositionWithPayments,
    ICancellationView,
} from "@/data/model/Cancellation";
import type { CancellationMode } from "@/data/model/Enums";
import type {
    IFullOrder,
    IOrder,
    IOrderItem,
    IOrderItemComment,
    IOrderOptionGroup,
    IOrderStockManagementStatus,
    IPayment,
    IPaymentItem,
    IPaymentItemOptionGroup,
    IPaymentMethodSplit,
    ITransaction,
    ITransactionMethodSplit,
    ITransactionOrder,
    ITransactionTable,
    IVatGroupDto,
} from "@/data/model/Order";
import type { IPaymentMethodReportRow, IPaymentMethodsReportData } from "@/data/model/PaymentMethodsReportData";
import type { ISalesReportData, ISalesReportFilters } from "@/data/model/SalesReportData";
import type { ICancellationsQryrdersQry } from "@/data/queries/order/CancellationsQry";
import type { IOrderQry, IOrdersQry } from "@/data/queries/order/OrdersQry";
import type { ITransactionMethodSplitsQry } from "@/data/queries/order/TransactionsQry";
import { DateTime } from "@/data/util/DateTime";

import {
    BaseServiceDao,
    type IDataSource,
    type IPaginatedResult,
    parseCollection,
    parseMultiLanguageText,
    parsePaginatedResult,
} from "./BaseServiceDao";

export class OrderDao extends BaseServiceDao implements IDataSource<IOrder> {
    public static className = "OrderDao";

    constructor() {
        super("order");
        this.isLocationDependent = true;
    }

    public async getSingle(id: string, qry: IOrderQry): Promise<IFullOrder> {
        return this.get(id, qry, OrderDao.parseFullOrder);
    }

    public async getData(qry: IOrdersQry): Promise<IPaginatedResult<IOrder>> {
        return this.get("", qry, (f) => parsePaginatedResult(f, OrderDao.parseOrder));
    }

    public async getTransactionMethodSplits(
        qry: ITransactionMethodSplitsQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<ITransactionMethodSplit>> {
        return this.get(
            "transaction-method-split/",
            qry,
            (f) => parsePaginatedResult(f, OrderDao.parseTransactionMethodSplit),
            abortSignal
        );
    }

    public async getTransaction(id: string): Promise<ITransaction> {
        return this.get("transaction/" + id, undefined, OrderDao.parseTransaction);
    }

    public async getCancellationView(qry: ICancellationsQryrdersQry): Promise<IPaginatedResult<ICancellationView>> {
        return this.get("cancellation/", qry, (f) => parsePaginatedResult(f, OrderDao.parseCancellationView));
    }

    public async addCancellation(orderId: string, data: IAddCancellationCmd): Promise<Array<ICancellation>> {
        return this.post(orderId + "/cancellation", data, (c) => parseCollection(c, OrderDao.parseCancellation));
    }

    public async generateCancellationPositions(orderId: string): Promise<Array<ICancellationPositionWithPayments>> {
        return this.get(orderId + "/cancellation/generate-positions", undefined, (c) =>
            parseCollection(c, OrderDao.parseCancellationPositionWithPayments)
        );
    }

    public sendOrderReceiptMail(orderId: string, data: ISendReceiptMailCmd) {
        return this.post(orderId + "/sendreceipt", data);
    }

    public sendPaymentReceiptMail(paymentId: string, data: ISendReceiptMailCmd) {
        return this.post("payment/" + paymentId + "/send-receipt", data);
    }

    public exportSalesReport(data: ISalesReportExportCmd) {
        return this.postDownloadFile("salesreport/export", data);
    }

    public generateSalesReportData(data: IGenerateSalesReportDataCmd): Promise<ISalesReportData> {
        return this.post("salesreport/data", data, OrderDao.parseSalesReportData);
    }

    public generatePaymentMethodsReportData(
        data: IGeneratePaymentMethodsReportDataCmd
    ): Promise<IPaymentMethodsReportData> {
        return this.post("payment-methods-report/data", data, OrderDao.parsePaymentMethodsReportData);
    }

    public async getOrderStockManagementStatus(
        orderId: string,
        mode: CancellationMode,
        positions?: IAddCancellationPositionData[]
    ): Promise<IOrderStockManagementStatus> {
        const data = {
            mode: mode,
            positions: positions,
        };
        return this.post(orderId + "/stock-management", data, OrderDao.parseOrderStockManagementStatus);
    }

    public exportOrders(data: IOrderExportCmd) {
        return this.postDownloadFile("export", data);
    }

    public exportTransactions(data: ITransactionExportCmd) {
        return this.postDownloadFile("transaction/export", data);
    }

    public static parseOrder(literal: any): IOrder {
        return {
            id: literal.id,
            created: DateTime.parseIsoDate(literal.created),
            currency: parseInt(literal.currency),
            consumption: parseFloat(literal.consumption),
            identityId: literal.identityId == null ? undefined : literal.identityId,
            tableId: literal.tableId == null ? undefined : literal.tableId,
            tableName: literal.tableName == null ? undefined : literal.tableName,
            locationId: literal.locationId,
            orderNumber: literal.orderNumber,
            dailyOrderIncrement:
                literal.dailyOrderIncrement == null ? undefined : parseInt(literal.dailyOrderIncrement),
            state: parseInt(literal.state),
            cancellationState: literal.cancellationState == null ? undefined : parseInt(literal.cancellationState),
            fulfillmentPositionDisplayMode: parseInt(literal.fulfillmentPositionDisplayMode),
            fulfillmentHandling: parseInt(literal.fulfillmentHandling),
            consumptionSite: parseInt(literal.consumptionSite),
            useCaseName: literal.useCaseName == null ? undefined : parseMultiLanguageText(literal.useCaseName),
            tip: parseFloat(literal.tip),
            vat: parseFloat(literal.vat),
            confirmedDateTime: literal.confirmDate == null ? undefined : DateTime.parseIsoDate(literal.confirmDate),
            payLater: literal.payLater,
            userName: literal.userName == null ? undefined : literal.userName,
            effectiveReadyDateTime:
                literal.effectiveReadyDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.effectiveReadyDateTime),
            effectiveFulfillmentDateTime:
                literal.effectiveFulfillmentDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.effectiveFulfillmentDateTime),
            billId: literal.billId == null ? undefined : literal.billId,
            customTableName: literal.customTableName == null ? undefined : literal.customTableName,
            fulfillmentTimeMode: parseInt(literal.fulfillmentTimeMode),
            serviceId: literal.serviceId == null ? undefined : literal.serviceId,
            scheduledFulfillmentDateTime:
                literal.scheduledFulfillmentDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.scheduledFulfillmentDateTime),
        };
    }

    public static parseFullOrder(literal: any): IFullOrder {
        return {
            ...OrderDao.parseOrder(literal),
            userEmail: literal.userEmail == null ? undefined : literal.userEmail,
            isTipCancelled: literal.isTipCancelled,
            items: parseCollection(literal.items, OrderDao.parseOrderItem),
            payments: parseCollection(literal.payments, OrderDao.parsePayment),
            currentVatGroups: parseCollection(literal.currentVatGroups, OrderDao.parseVatGroupDto),
            totalAmount: parseFloat(literal.totalAmount),
            isBillOpen: literal.isBillOpen,
            ordererEmail: literal.ordererEmail == null ? undefined : literal.ordererEmail,
            ordererPhoneNumber: literal.ordererPhoneNumber == null ? undefined : literal.ordererPhoneNumber,
            orderedByOperatorName: literal.orderedByOperatorName == null ? undefined : literal.orderedByOperatorName,
            cancellations:
                literal.cancellations == null
                    ? undefined
                    : parseCollection(literal.cancellations, OrderDao.parseCancellation),
            totalCancellationAmount: parseFloat(literal.totalCancellationAmount),
            totalPaidCancellationAmount: parseFloat(literal.totalPaidCancellationAmount),
            currentTotalDiscountAmount: parseFloat(literal.currentTotalDiscountAmount),
            currentOrderDiscountAmount: parseFloat(literal.currentOrderDiscountAmount),
            totalPaidAmount: parseFloat(literal.totalPaidAmount),
            totalCancelledAmount: parseFloat(literal.totalCancelledAmount),
        };
    }

    public static parsePaymentMethodsReportData(literal: any): IPaymentMethodsReportData {
        return {
            data: parseCollection(literal.data, OrderDao.parsePaymentMethodReportRow),
            filters: OrderDao.parseSalesReportFilters(literal.filters),
            totals: OrderDao.parsePaymentMethodReportRow(literal.totals),
        };
    }

    public static parseSalesReportData(literal: any): ISalesReportData {
        return {
            data: literal.data,
            totals: literal.totals,
            filters: OrderDao.parseSalesReportFilters(literal.filters),
        };
    }

    public static parsePaymentMethodReportRow(literal: any): IPaymentMethodReportRow {
        return {
            paymentMethodId: literal.paymentMethodId,
            paymentMethodName: literal.paymentMethodName,
            paymentMethodGroupName: literal.paymentMethodGroupName,
            cancelledQuantity: parseFloat(literal.cancelledQuantity),
            grossSalesInclTips: parseFloat(literal.grossSalesInclTips),
            quantityNet: parseFloat(literal.quantityNet),
            quantity: parseFloat(literal.quantity),
            tipNet: parseFloat(literal.tipNet),
            grossNetSalesAvg: parseFloat(literal.grossNetSalesAvg),
            grossNetSalesPercent: parseFloat(literal.grossNetSalesPercent),
        };
    }

    public static parseSalesReportFilters(literal: any): ISalesReportFilters {
        return {
            startDate: DateTime.parseIsoDate(literal.startDate),
            endDate: DateTime.parseIsoDate(literal.endDate),
            userName: literal.userName == null ? undefined : literal.userName,
            rooms: literal.rooms == null ? undefined : parseCollection(literal.rooms, RoomDao.parseRoom),
            fulfillmentGroups:
                literal.fulfillmentGroups == null
                    ? undefined
                    : parseCollection(literal.fulfillmentGroups, CatalogDao.parseFulfillmentGroup),
            offers: literal.offers == null ? undefined : parseCollection(literal.offers, CatalogDao.parseOffer),
            filterByEndUsers: literal.filterByEndUsers,
        };
    }

    public static parseOrderItem(literal: any): IOrderItem {
        return {
            id: literal.id,
            articleId: literal.articleId == null ? undefined : literal.articleId,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            quantity: parseInt(literal.quantity),
            singlePrice: parseFloat(literal.singlePrice),
            singleDiscountedPriceWithOptions:
                literal.singleDiscountedPriceWithOptions == null
                    ? undefined
                    : parseFloat(literal.singleDiscountedPriceWithOptions),
            vat: literal.vat == null ? undefined : parseFloat(literal.vat),
            options: parseCollection(literal.options, OrderDao.parseOrderOptionGroup),
            isPaid: literal.isPaid,
            cancelledQuantity: parseInt(literal.cancelledQuantity),
            isCancelled: literal.isCancelled,
            originalTotalAmount: parseFloat(literal.originalTotalAmount),
            currentTotalAmount: parseFloat(literal.currentTotalAmount),
            originalTotalQuantity: parseInt(literal.originalTotalQuantity),
            currentTotalQuantity: parseInt(literal.currentTotalQuantity),

            orderId: literal.orderId,
            paidCancelledQuantity: parseInt(literal.paidCancelledQuantity),
            paidQuantity: parseInt(literal.paidQuantity),
            currentTotalUnpaidQuantity: parseInt(literal.currentTotalUnpaidQuantity),
            comment: literal.comment == null ? undefined : OrderDao.parseOrderItemComment(literal.comment),

            hasDiscount: literal.hasDiscount,
            singlePriceAfterDiscount: parseFloat(literal.singlePriceAfterDiscount),
            lastDiscountType: literal.lastDiscountType == null ? undefined : parseInt(literal.lastDiscountType),
            currentTotalAmountAfterDiscount: parseFloat(literal.currentTotalAmountAfterDiscount),
        };
    }

    public static parseOrderItemComment(literal: any): IOrderItemComment {
        return {
            identityId: literal.identityId,
            text: literal.text,
        };
    }

    public static parseVatGroupDto(literal: any): IVatGroupDto {
        return {
            totalAmount: parseFloat(literal.totalAmount),
            vatValue: parseFloat(literal.vatValue),
        };
    }

    public static parseOrderOptionGroup(literal: any): IOrderOptionGroup {
        return {
            id: literal.id,
            displayName: literal.displayName,
            name: literal.name == null ? undefined : parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            items: parseCollection(literal.items, OrderDao.parseOrderItem),
        };
    }

    public static parsePaymentItemOptionGroup(literal: any): IPaymentItemOptionGroup {
        return {
            id: literal.id,
            displayName: literal.displayName,
            name: literal.name == null ? undefined : parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            items: parseCollection(literal.items, OrderDao.parsePaymentItem),
        };
    }

    public static parsePayment(literal: any): IPayment {
        return {
            id: literal.id,
            amount: parseFloat(literal.amount),
            created: DateTime.parseIsoDate(literal.created),
            confirmDate: literal.confirmDate == null ? undefined : DateTime.parseIsoDate(literal.confirmDate),
            currency: parseInt(literal.currency),
            provider: literal.provider == null ? undefined : parseInt(literal.provider),
            state: parseInt(literal.state),
            brand: literal.brand == null ? undefined : literal.brand,
            holder: literal.holder == null ? undefined : literal.holder,
            ccDigits: literal.ccDigits == null ? undefined : literal.ccDigits,
            type: literal.type == null ? undefined : literal.type,
            region: literal.region == null ? undefined : literal.region,
            declineReason: literal.declineReason == null ? undefined : literal.declineReason,
            ccExpiryMonth: literal.ccExpiryMonth == null ? undefined : parseInt(literal.ccExpiryMonth),
            ccExpiryYear: literal.ccExpiryYear == null ? undefined : parseInt(literal.ccExpiryYear),
            paymentItems: parseCollection(literal.paymentItems, OrderDao.parsePaymentItem),
            paymentMethodSplits: parseCollection(literal.paymentMethodSplits, OrderDao.parsePaymentMethodSplit),
            tip: literal.tip == null ? undefined : parseFloat(literal.tip),
            predefinedPaymentMethodImagePath:
                literal.predefinedPaymentMethodImagePath == null ? undefined : literal.predefinedPaymentMethodImagePath,
            customLocationPaymentMethodId:
                literal.customLocationPaymentMethodId == null ? undefined : literal.customLocationPaymentMethodId,
            customPaymentMethodImagePath:
                literal.customPaymentMethodImagePath == null ? undefined : literal.customPaymentMethodImagePath,
            actualPaymentMethodName:
                literal.actualPaymentMethodName == null ? undefined : literal.actualPaymentMethodName,
            actualPaymentMethodImagePath:
                literal.actualPaymentMethodImagePath == null ? undefined : literal.actualPaymentMethodImagePath,
            merchantId: literal.merchantId == null ? undefined : literal.merchantId,
            cancellationState: literal.cancellationState == null ? undefined : parseInt(literal.cancellationState),
            client: literal.client == null ? undefined : parseInt(literal.client),
            externalPaymentId: literal.externalPaymentId,
        };
    }

    public static parsePaymentMethodSplit(literal: any): IPaymentMethodSplit {
        return {
            id: literal.id,
            paymentId: literal.paymentId,
            code: literal.code,
            state: parseInt(literal.state),
            client: literal.client == null ? undefined : parseInt(literal.client),
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
            confirmDate: literal.confirmDate == null ? undefined : DateTime.parseIsoDate(literal.confirmDate),
            revenueEffective: literal.revenueEffective,
            amount: parseFloat(literal.amount),
            declineReason: literal.declineReason == null ? undefined : literal.declineReason,
            provider: literal.provider == null ? undefined : parseInt(literal.provider),
            merchantId: literal.merchantId == null ? undefined : literal.merchantId,
            brand: literal.brand == null ? undefined : literal.brand,
            holder: literal.holder == null ? undefined : literal.holder,
            ccDigits: literal.ccDigits == null ? undefined : literal.ccDigits,
            region: literal.region == null ? undefined : literal.region,
            predefinedPaymentMethodImagePath:
                literal.predefinedPaymentMethodImagePath == null ? undefined : literal.predefinedPaymentMethodImagePath,
            customLocationPaymentMethodId:
                literal.customLocationPaymentMethodId == null ? undefined : literal.customLocationPaymentMethodId,
            customPaymentMethodImagePath:
                literal.customPaymentMethodImagePath == null ? undefined : literal.customPaymentMethodImagePath,
            actualPaymentMethodName:
                literal.actualPaymentMethodName == null ? undefined : literal.actualPaymentMethodName,
            actualPaymentMethodImagePath:
                literal.actualPaymentMethodImagePath == null ? undefined : literal.actualPaymentMethodImagePath,
            cancellationState: literal.cancellationState == null ? undefined : parseInt(literal.cancellationState),
            isDiscountPayment: literal.isDiscountPayment,
        };
    }

    public static parseCancellationPositionWithPayments(literal: any): ICancellationPositionWithPayments {
        return {
            orderItemId: literal.orderItemId,
            unpaidQuantity: parseInt(literal.unpaidQuantity),
            maxCancellableQuantity: parseInt(literal.maxCancellableQuantity),
            singlePrice: parseFloat(literal.singlePrice),
            orderItemInternalName: literal.orderItemInternalName == null ? undefined : literal.orderItemInternalName,
            orderItemName: literal.orderItemName == null ? undefined : parseMultiLanguageText(literal.orderItemName),
            positionType: parseInt(literal.positionType),
            payments: parseCollection(literal.payments, OrderDao.parseCancellationPositionPayment),
            cancelledUnpaidQuantity: parseFloat(literal.cancelledUnpaidQuantity),
        };
    }

    public static parseCancellationPositionPayment(literal: any): ICancellationPositionPayment {
        return {
            createdBy: literal.createdBy,
            created: DateTime.parseIsoDate(literal.created),
            confirmedDateTime:
                literal.confirmedDateTime == null ? undefined : DateTime.parseIsoDate(literal.confirmedDateTime),
            provider: literal.provider == null ? undefined : parseInt(literal.provider),
            quantity: parseInt(literal.quantity),
            brand: literal.brand == null ? undefined : literal.brand,
            type: literal.type,
            region: literal.region == null ? undefined : literal.region,
            ccDigits: literal.ccDigits == null ? undefined : literal.ccDigits,
            totalInclTip: parseFloat(literal.totalInclTip),
            paymentMethodName: literal.paymentMethodName,
            paymentId: literal.paymentId,
            cancelledQuantity: parseFloat(literal.cancelledQuantity),
            customPaymentMethodImagePath:
                literal.customPaymentMethodImagePath == null ? undefined : literal.customPaymentMethodImagePath,
            actualPaymentMethodImagePath:
                literal.actualPaymentMethodImagePath == null ? undefined : literal.actualPaymentMethodImagePath,
            predefinedPaymentMethodImagePath:
                literal.predefinedPaymentMethodImagePath == null ? undefined : literal.predefinedPaymentMethodImagePath,
        };
    }

    public static parsePaymentItem(literal: any): IPaymentItem {
        return {
            id: literal.id,
            orderItemId: literal.orderItemId,
            quantity: literal.quantity,
            amount: literal.amount,
            internalName: literal.internalName == null ? undefined : literal.internalName,
            name: literal.name == null ? undefined : parseMultiLanguageText(literal.name),
            options: parseCollection(literal.options, OrderDao.parseOrderOptionGroup),
            cancelledQty: parseInt(literal.cancelledQty),
            isCancelled: literal.isCancelled,
            currentQty: literal.currentQty,
            itemSinglePrice: literal.itemSinglePrice,
            itemSinglePriceAfterDiscount: literal.itemSinglePriceAfterDiscount,
            lastDiscountType: literal.lastDiscountType == null ? undefined : parseInt(literal.lastDiscountType),
            hasDiscount: literal.hasDiscount,
        };
    }

    public static parseTransaction(literal: any): ITransaction {
        return {
            ...OrderDao.parsePayment(literal),
            userId: literal.userId == null ? undefined : literal.userId,
            userName: literal.userName == null ? undefined : literal.userName,
            locationId: literal.locationId == null ? undefined : literal.locationId,
            locationName: literal.locationName == null ? undefined : literal.locationName,
            locationLanguage: literal.locationLanguage == null ? undefined : parseInt(literal.locationLanguage),
            providerId: literal.providerId,
            transactionOrders: parseCollection(literal.transactionOrders, OrderDao.parseTransactionOrder),
            transactionTables: parseCollection(literal.transactionTables, OrderDao.parseTransactionTable),
            tableId: literal.tableId == null ? undefined : literal.tableId,
            cancelledOrdersTip: parseFloat(literal.cancelledOrdersTip),
            payUseCaseName: literal.payUseCaseName == null ? undefined : parseMultiLanguageText(literal.payUseCaseName),
            vatGroups: parseCollection(literal.vatGroups, OrderDao.parseVatGroupDto),
            billId: literal.billId == null ? undefined : literal.billId,
            isBillOpen: literal.isBillOpen,
            tableName: literal.tableName == null ? undefined : literal.tableName,
            customTableName: literal.customTableName == null ? undefined : literal.customTableName,
            consumption: parseFloat(literal.consumption),
            orderLevelDiscountAmount: parseFloat(literal.orderLevelDiscountAmount),
            totalPaid: parseFloat(literal.totalPaid),
        };
    }

    public static parseTransactionMethodSplit(literal: any): ITransactionMethodSplit {
        return {
            ...OrderDao.parsePaymentMethodSplit(literal),
            identityId: literal.identityId == null ? undefined : literal.identityId,
            userName: literal.userName == null ? undefined : literal.userName,
            locationId: literal.locationId,
            locationName: literal.locationName,
            locationInternalName: literal.locationInternalName == null ? undefined : literal.locationInternalName,
            locationLanguage: literal.locationLanguage == null ? undefined : parseInt(literal.locationLanguage),
            providerId: literal.providerId,
            transactionOrders: parseCollection(literal.transactionOrders, OrderDao.parseTransactionOrder),
            transactionTables: parseCollection(literal.transactionTables, OrderDao.parseTransactionTable),
            tableId: literal.tableId == null ? undefined : literal.tableId,
            cancelledOrdersTip: parseFloat(literal.cancelledOrdersTip),
            payUseCaseName: literal.payUseCaseName == null ? undefined : parseMultiLanguageText(literal.payUseCaseName),
            vatGroups: parseCollection(literal.vatGroups, OrderDao.parseVatGroupDto),
            billId: literal.billId == null ? undefined : literal.billId,
            isBillOpen: literal.isBillOpen,
            tableName: literal.tableName == null ? undefined : literal.tableName,
        };
    }

    public static parseTransactionOrder(literal: any): ITransactionOrder {
        return {
            orderId: literal.orderId,
            orderNumber: literal.orderNumber,
            dailyOrderIncrement: literal.dailyOrderIncrement == null ? null : literal.dailyOrderIncrement,
            tableId: literal.tableId == null ? undefined : literal.tableId,
            tableName: literal.tableName == null ? undefined : literal.tableName,
        };
    }

    public static parseTransactionTable(literal: any): ITransactionTable {
        return {
            tableId: literal.tableId,
            tableName: literal.tableName,
        };
    }

    public static parseCancellation(literal: any): ICancellation {
        return {
            id: literal.id,
            created: DateTime.parseIsoDate(literal.created),
            mode: parseInt(literal.mode),
            reason: literal.reason == null ? undefined : parseInt(literal.reason),
            userId: literal.userId == null ? undefined : literal.userId,
            userName: literal.userName == null ? undefined : literal.userName,
            orderId: literal.orderId,
            orderNumber: literal.orderNumber,
            positions: parseCollection(literal.positions, OrderDao.parseCancellationPosition),
            cancelledPayments: parseCollection(literal.cancelledPayments, OrderDao.parseCancellationPayment),
            amount: parseFloat(literal.amount),
            merchantId: literal.merchantId == null ? undefined : literal.merchantId,
        };
    }

    public static parseCancellationView(literal: any): ICancellationView {
        return {
            id: literal.id,
            created: DateTime.parseIsoDate(literal.created),
            mode: parseInt(literal.mode),
            reason: literal.reason == null ? undefined : parseInt(literal.reason),
            userId: literal.userId == null ? undefined : literal.userId,
            userName: literal.userName == null ? undefined : literal.userName,
            orderId: literal.orderId,
            orderCode: literal.orderCode,
            positions: parseCollection(literal.positions, OrderDao.parseCancellationPosition),
            amount: parseFloat(literal.amount),
            merchantId: literal.merchantId == null ? undefined : literal.merchantId,
            region: literal.region == null ? undefined : literal.region,
            providerId: literal.providerId == null ? undefined : literal.providerId,
            provider: literal.provider == null ? undefined : parseInt(literal.provider),
            refundMode: literal.refundMode == null ? undefined : parseInt(literal.refundMode),
            refundPspId: literal.refundPspId == null ? undefined : literal.refundPspId,
            brand: literal.brand == null ? undefined : literal.brand,
            holder: literal.holder == null ? undefined : literal.holder,
            ccDigits: literal.ccDigits == null ? undefined : literal.ccDigits,
            ccExpiryMonth: literal.ccExpiryMonth == null ? undefined : parseInt(literal.ccExpiryMonth),
            ccExpiryYear: literal.ccExpiryYear == null ? undefined : parseInt(literal.ccExpiryYear),
            actualPaymentMethodImagePath:
                literal.actualPaymentMethodImagePath == null ? undefined : literal.actualPaymentMethodImagePath,
            predefinedPaymentMethodImagePath:
                literal.predefinedPaymentMethodImagePath == null ? undefined : literal.predefinedPaymentMethodImagePath,
            customPaymentMethodImagePath:
                literal.customPaymentMethodImagePath == null ? undefined : literal.customPaymentMethodImagePath,
            externalPaymentId: literal.externalPaymentId == null ? undefined : literal.externalPaymentId,
            paymentId: literal.paymentId == null ? undefined : literal.paymentId,
            paymentCode: literal.paymentCode == null ? undefined : literal.paymentCode,
            paymentClient: literal.paymentClient == null ? undefined : parseInt(literal.paymentClient),
            isDiscountPayment: literal.isDiscountPayment == null ? undefined : literal.isDiscountPayment,
        };
    }

    public static parseCancellationPayment(literal: any): ICancellationPayment {
        return {
            refundMode: literal.refundMode == null ? undefined : parseInt(literal.refundMode),
            provider: literal.provider == null ? undefined : parseInt(literal.provider),
            refundPspId: literal.refundPspId == null ? undefined : literal.refundPspId,
            brand: literal.brand == null ? undefined : literal.brand,
            holder: literal.holder == null ? undefined : literal.holder,
            ccDigits: literal.ccDigits == null ? undefined : literal.ccDigits,
            type: literal.type == null ? undefined : literal.type,
            region: literal.region == null ? undefined : literal.region,
            ccExpiryMonth: literal.ccExpiryMonth == null ? undefined : parseInt(literal.ccExpiryMonth),
            ccExpiryYear: literal.ccExpiryYear == null ? undefined : parseInt(literal.ccExpiryYear),
            customPaymentMethodImagePath:
                literal.customPaymentMethodImagePath == null ? undefined : literal.customPaymentMethodImagePath,
            actualPaymentMethodImagePath:
                literal.actualPaymentMethodImagePath == null ? undefined : literal.actualPaymentMethodImagePath,
            predefinedPaymentMethodImagePath:
                literal.predefinedPaymentMethodImagePath == null ? undefined : literal.predefinedPaymentMethodImagePath,
        };
    }

    public static parseCancellationPosition(literal: any): ICancellationPosition {
        return {
            id: literal.id,
            amount: parseFloat(literal.amount),
            positionType: parseInt(literal.positionType),
            orderItemInternalName: literal.orderItemInternalName == null ? undefined : literal.orderItemInternalName,
            orderItemName: literal.orderItemName == null ? undefined : parseMultiLanguageText(literal.orderItemName),
            quantity: parseInt(literal.quantity),
            orderItemId: literal.orderItemId == null ? undefined : literal.orderItemId,
        };
    }

    public static parseOrderStockManagementStatus(literal: any): IOrderStockManagementStatus {
        return {
            orderId: literal.orderId,
            hasAvailableItemsWithStockManagement: literal.hasAvailableItemsWithStockManagement,
        };
    }
}
